/**
 * Cliente HTTP **único** de la aplicación (`lib/api`).
 *
 * Toda petición a `/api/v1` pasa por aquí (`docs/frontend_architecture.md`,
 * Pr. 2): ningún componente ni hook hace `fetch`/`ky` por su cuenta. Las
 * llamadas de dominio van en el `api.ts` de cada feature, sobre `request`.
 * Responsabilidades centralizadas en esta capa:
 *
 *  - `credentials: 'include'`: las cookies httpOnly de sesión viajan solas.
 *  - `beforeRequest`: inyecta `X-Sede-Id` (sede activa, de `lib/sede`) y
 *    `X-CSRF-TOKEN` (cookie CSRF no httpOnly, double-submit de la #41).
 *  - `afterResponse`: ante un `401`, intenta `POST /auth/refresh` **una sola
 *    vez** y reintenta la request original; evita bucles (no refresca el propio
 *    refresh ni reintenta más de una vez).
 *  - `request(...)`: envoltura tipada que traduce el contrato de error
 *    `{error_code, message}` del backend a la excepción `ApiError`. Es la
 *    superficie pública: los llamadores solo ven `ApiError`, nunca el
 *    `HTTPError` crudo de `ky`.
 */
import ky, { HTTPError, type KyInstance, type Options } from "ky";

import { getActiveSedeId } from "@/lib/sede";

import { CSRF_HEADER_NAME, readCsrfToken } from "./csrf";
import { ApiError, isApiErrorBody } from "./errors";

/**
 * Prefijo de la API headless, resuelto a una URL **absoluta**.
 *
 * Dos modos, según cómo se despliegue (ver `docs/despliegue_separado.md`):
 *
 *  - **Mismo origen (default)**: se construye con el origen actual + `/api/v1`.
 *    Cubre el dev (el proxy de Vite manda `/api` a Flask) y cualquier despliegue
 *    en el que la SPA y el API compartan host. Sin fricción de CORS.
 *  - **Origen separado (SPA en un static host distinto del API)**: si está
 *    definida la variable de build `VITE_API_BASE_URL` (p. ej.
 *    `https://api.midominio.com`), se usa como base. El navegador hace entonces
 *    una petición cross-origin con credenciales, que el backend habilita por CORS
 *    (`CORS_ORIGINS` + `supports_credentials`) y cookies `SameSite=None; Secure`.
 *
 * Es una URL absoluta (no un prefijo relativo) para evitar la ambigüedad de
 * resolver rutas relativas, que rompe interceptores y tests. La variable es
 * **aditiva**: sin ella, el comportamiento es el de mismo origen de siempre (dev
 * y tests intactos).
 */
export const API_PREFIX = resolveApiPrefix();

function resolveApiPrefix(): string {
  // Base explícita de build (despliegue separado). `import.meta.env` la inyecta
  // Vite en build; si no está definida, se cae al mismo origen.
  const configuredBase = import.meta.env.VITE_API_BASE_URL;
  if (configuredBase) {
    // Sin barra final duplicada: VITE_API_BASE_URL puede traerla o no.
    return `${configuredBase.replace(/\/$/, "")}/api/v1`;
  }
  // En el navegador y en jsdom hay `window.location`; fuera (SSR) caemos a una
  // base inocua de localhost (no se hacen requests reales en ese contexto).
  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "http://localhost";
  return `${origin}/api/v1`;
}

/** Ruta (relativa al prefijo) del endpoint de refresh. */
const REFRESH_PATH = "auth/refresh";

/**
 * Marca, en una cabecera de la request, que ésta ya es el reintento posterior a
 * un refresh. Si ese reintento vuelve a dar 401, NO se refresca de nuevo: corta
 * el bucle. Se usa una cabecera (no un `WeakSet`) porque `ky` clona la `Request`
 * internamente en cada llamada —el objeto que llega a `afterResponse` no es el
 * mismo que pasamos—, pero las cabeceras sí se conservan a través de esos clones.
 * Es una cabecera interna e inocua para el backend (la ignora).
 */
const RETRY_HEADER = "X-Retry-After-Refresh";

/** Inyecta `X-Sede-Id` y `X-CSRF-TOKEN` en cada request saliente. */
function injectHeaders(request: Request): void {
  const sedeId = getActiveSedeId();
  if (sedeId !== null) {
    request.headers.set("X-Sede-Id", String(sedeId));
  }
  const csrf = readCsrfToken();
  if (csrf !== null) {
    request.headers.set(CSRF_HEADER_NAME, csrf);
  }
}

/**
 * Construye la instancia de `ky`. Se aísla en una función para que los tests
 * puedan razonar sobre una instancia única sin estado compartido entre módulos.
 */
function createClient(): KyInstance {
  return ky.create({
    prefixUrl: API_PREFIX,
    credentials: "include",
    retry: 0, // el reintento tras 401 lo gestiona nuestro hook, no `ky`.
    hooks: {
      beforeRequest: [
        (request) => {
          injectHeaders(request);
        },
      ],
      afterResponse: [
        async (request, _options, response) => {
          // Solo nos interesa el 401 (sesión expirada -> intentar refrescar).
          if (response.status !== 401) {
            return response;
          }
          // No refrescar el propio refresh, ni reintentar dos veces: evita bucles.
          const isRefreshCall = request.url.includes(REFRESH_PATH);
          const isRetry = request.headers.get(RETRY_HEADER) === "1";
          if (isRefreshCall || isRetry) {
            return response;
          }

          try {
            // Refresca el access token (el refresh viaja en su cookie httpOnly;
            // `beforeRequest` adjunta el `X-CSRF-TOKEN`).
            await client.post(REFRESH_PATH);
          } catch {
            // El refresh falló: propagamos el 401 original (la guarda de auth
            // redirige a login). No relanzamos aquí para no enmascarar el error.
            return response;
          }

          // Reintenta la request original UNA vez, marcándola para no repetir.
          const retryRequest = request.clone();
          retryRequest.headers.set(RETRY_HEADER, "1");
          return client(retryRequest);
        },
      ],
    },
  });
}

/** Instancia interna de `ky`. No se usa directamente fuera de `lib/api`. */
const client: KyInstance = createClient();

/**
 * Traduce un `HTTPError` de `ky` a `ApiError` leyendo el cuerpo `{error_code,
 * message}`. Si el cuerpo no cumple el contrato (p. ej. un 502 de proxy con
 * HTML), sintetiza un `ApiError` con un código genérico y el status real.
 *
 * `beforeError` de `ky` no sirve para esto: su firma obliga a devolver un
 * `HTTPError`, no una subclase propia. Por eso la traducción vive aquí, en la
 * frontera pública `request(...)`.
 */
async function toApiError(error: HTTPError): Promise<ApiError> {
  const { response } = error;
  let body: unknown = null;
  try {
    body = await response.clone().json();
  } catch {
    body = null;
  }

  if (isApiErrorBody(body)) {
    return new ApiError({
      error_code: body.error_code,
      message: body.message,
      status: response.status,
      details: body.details,
    });
  }

  return new ApiError({
    error_code: "http_error",
    message: `Error HTTP ${response.status}.`,
    status: response.status,
  });
}

/**
 * Realiza una petición a la API y devuelve la respuesta cruda de `ky`.
 *
 * Punto único de salida del cliente: cualquier `HTTPError` se traduce a
 * `ApiError`. Los helpers `api.get/post/...` se construyen encima para parsear
 * JSON. Una llamada de dominio NUNCA llama a `ky` directamente: usa esto.
 */
export async function request(url: string, options?: Options): Promise<Response> {
  try {
    return await client(url, options);
  } catch (error) {
    if (error instanceof HTTPError) {
      throw await toApiError(error);
    }
    throw error;
  }
}

/** Verbos HTTP del cliente, cada uno devolviendo la `Response` ya validada. */
export const api = {
  get: (url: string, options?: Options) =>
    request(url, { ...options, method: "get" }),
  post: (url: string, options?: Options) =>
    request(url, { ...options, method: "post" }),
  put: (url: string, options?: Options) =>
    request(url, { ...options, method: "put" }),
  patch: (url: string, options?: Options) =>
    request(url, { ...options, method: "patch" }),
  delete: (url: string, options?: Options) =>
    request(url, { ...options, method: "delete" }),
} as const;
